import { APIClient } from "../helpers/api_helper";
import { ApiResponse } from "../types/api";

const api = new APIClient();

const RESOURCE = "/integrations";

export interface ConfigSchemaProperty {
  type?: string;
  title?: string;
}

export interface ConfigSchema {
  type?: string;
  required?: string[];
  properties?: Record<string, ConfigSchemaProperty>;
}

export interface IntegrationProvider {
  slug: string;
  name: string;
  category: string;
  sort_order: number;
  is_recommended: boolean;
  tagline: string | null;
  capabilities: string[];
  config_schema: ConfigSchema | null;
  installed: boolean;
}

export interface TenantIntegration {
  id: number;
  provider_slug: string;
  capability: string;
  unit_id: number | null;
  unit: { id: number; name: string } | null;
  enabled: boolean;
  /** API returns `configured`; treated as credentials already saved (never exposed). */
  configured: boolean;
  settings: Record<string, unknown>;
}

export interface InstallIntegrationPayload {
  provider_slug: string;
  capability: string;
  unit_id?: number | null;
  credentials: Record<string, string>;
  settings?: Record<string, unknown>;
  enabled?: boolean;
}

export interface UpdateIntegrationPayload {
  provider_slug: string;
  capability: string;
  unit_id?: number | null;
  credentials?: Record<string, string>;
  settings?: Record<string, unknown>;
  enabled?: boolean;
}

export interface UninstallIntegrationParams {
  provider_slug: string;
  capability: string;
  unit_id?: number | null;
}

export const getProviders = (): Promise<ApiResponse<IntegrationProvider[]>> =>
  api.get<ApiResponse<IntegrationProvider[]>>(`${RESOURCE}/providers`);

export const getInstalledIntegrations = (): Promise<
  ApiResponse<TenantIntegration[]>
> => api.get<ApiResponse<TenantIntegration[]>>(`${RESOURCE}/installed`);

/** @deprecated Prefer `getInstalledIntegrations` — alias kept for Integrations page. */
export const getInstalled = getInstalledIntegrations;

export const install = (
  payload: InstallIntegrationPayload
): Promise<ApiResponse<TenantIntegration>> =>
  api.create<ApiResponse<TenantIntegration>>(`${RESOURCE}/installed`, payload);

export const update = (
  payload: UpdateIntegrationPayload
): Promise<ApiResponse<TenantIntegration>> =>
  api.update<ApiResponse<TenantIntegration>>(`${RESOURCE}/installed`, payload);

export const uninstall = (
  params: UninstallIntegrationParams
): Promise<ApiResponse<Record<string, never>>> => {
  const query = new URLSearchParams({
    provider_slug: params.provider_slug,
    capability: params.capability,
  });

  if (params.unit_id !== undefined && params.unit_id !== null) {
    query.set("unit_id", String(params.unit_id));
  }

  return api.delete<ApiResponse<Record<string, never>>>(
    `${RESOURCE}/installed?${query.toString()}`
  );
};

export interface GoogleReserveSyncEvent {
  at: string;
  event: string;
  direction: string;
  ok: boolean;
  summary: string;
}

export interface GoogleReserveStatus {
  installed: boolean;
  enabled: boolean;
  environment: "sandbox" | "production" | string;
  platform_configured: boolean;
  readiness?: {
    sandbox: boolean;
    booking_server: boolean;
    partner_id: boolean;
    feeds: boolean;
    rtu: boolean;
    sandbox_review_ready: boolean;
    production_ready: boolean;
  };
  preparation?: {
    active: boolean;
    eta_business_days: number;
  };
  code_request?: {
    available: boolean;
    last_requested_at: string | null;
    available_again_at?: string | null;
    ticket_id?: number | null;
    ticket_public_number?: string | null;
  };
  issued_code?: {
    available: boolean;
    merchant_id_masked: string | null;
    issued_at: string | null;
  };
  mapping: {
    unit_id: number | null;
    unit_name: string | null;
    has_unit_code?: boolean;
    merchant_id_masked?: string | null;
    /** @deprecated Prefer merchant_id_masked */
    merchant_id?: string | null;
  } | null;
  sync_history: GoogleReserveSyncEvent[];
}

export const getGoogleReserveStatus = (
  unitId?: number | null
): Promise<ApiResponse<GoogleReserveStatus>> => {
  const query =
    unitId !== undefined && unitId !== null
      ? `?unit_id=${encodeURIComponent(String(unitId))}`
      : "";

  return api.get<ApiResponse<GoogleReserveStatus>>(
    `${RESOURCE}/google-reserve/status${query}`
  );
};

export const requestGoogleReserveSync = (
  unitId?: number | null
): Promise<ApiResponse<{ queued: boolean }>> =>
  api.create<ApiResponse<{ queued: boolean }>>(
    `${RESOURCE}/google-reserve/sync`,
    unitId !== undefined && unitId !== null ? { unit_id: unitId } : {}
  );

export const requestGoogleReserveUnitCode = (
  unitId?: number | null
): Promise<
  ApiResponse<{
    queued: boolean;
    available_again_at: string | null;
    ticket_id: number | null;
    ticket_public_number: string | null;
  }>
> =>
  api.create<
    ApiResponse<{
      queued: boolean;
      available_again_at: string | null;
      ticket_id: number | null;
      ticket_public_number: string | null;
    }>
  >(
    `${RESOURCE}/google-reserve/request-code`,
    unitId !== undefined && unitId !== null ? { unit_id: unitId } : {}
  );

export const claimGoogleReserveIssuedCode = (
  unitId: number
): Promise<
  ApiResponse<{ unit_code: string; unit_id: number; merchant_id_masked: string }>
> =>
  api.create<
    ApiResponse<{ unit_code: string; unit_id: number; merchant_id_masked: string }>
  >(`${RESOURCE}/google-reserve/claim-issued-code`, { unit_id: unitId });

export interface MetaCommerceSyncEvent {
  at: string;
  event: string;
  direction: string;
  ok: boolean;
  summary: string;
}

export interface MetaCommerceStatus {
  installed: boolean;
  enabled: boolean;
  catalog_configured: boolean;
  waba_ready: boolean;
  messaging_outbound: boolean;
  last_sync_at: string | null;
  counts: {
    ok: number;
    failed: number;
    skipped: number;
    pending: number;
    missing_photo?: number;
  };
  sync_history: MetaCommerceSyncEvent[];
}

export interface MetaCommerceSyncFilters {
  unit_id?: number;
  category_id?: number;
  brand?: string;
  missing_photo_only?: boolean;
}

export interface MetaCommercePreview {
  total: number;
  with_photo: number;
  missing_photo: number;
  soft_cap: number;
  exceeds_soft_cap: boolean;
  sample: Array<{
    id: number;
    name: string;
    has_photo: boolean;
    brand: string | null;
  }>;
}

export interface MetaCommerceProductMessage {
  product_id: number;
  retailer_id: string;
  text: string;
  whatsapp_url: string;
  has_photo: boolean;
  synced: boolean;
}

export const getMetaCommerceStatus = (): Promise<
  ApiResponse<MetaCommerceStatus>
> =>
  api.get<ApiResponse<MetaCommerceStatus>>(
    `${RESOURCE}/meta-commerce/status`
  );

export const getMetaCommercePreview = (
  filters: MetaCommerceSyncFilters = {}
): Promise<ApiResponse<MetaCommercePreview>> => {
  const params = new URLSearchParams();
  if (filters.unit_id != null) params.set("unit_id", String(filters.unit_id));
  if (filters.category_id != null)
    params.set("category_id", String(filters.category_id));
  if (filters.brand) params.set("brand", filters.brand);
  if (filters.missing_photo_only) params.set("missing_photo_only", "1");
  const query = params.toString() ? `?${params.toString()}` : "";
  return api.get<ApiResponse<MetaCommercePreview>>(
    `${RESOURCE}/meta-commerce/preview${query}`
  );
};

export const requestMetaCommerceSync = (
  filters: MetaCommerceSyncFilters = {}
): Promise<ApiResponse<{ queued: boolean }>> =>
  api.create<ApiResponse<{ queued: boolean }>>(
    `${RESOURCE}/meta-commerce/sync`,
    filters
  );

export const getMetaCommerceProductMessage = (
  productId: number
): Promise<ApiResponse<MetaCommerceProductMessage>> =>
  api.get<ApiResponse<MetaCommerceProductMessage>>(
    `${RESOURCE}/meta-commerce/products/${productId}/message`
  );

export const createMetaCommerceCartDraft = (payload: {
  unit_id: number;
  client_id: number;
  items: Array<{ product_id?: number; retailer_id?: string; quantity?: number }>;
}): Promise<ApiResponse<{ order_id: number; items_added: number; skipped: number }>> =>
  api.create<
    ApiResponse<{ order_id: number; items_added: number; skipped: number }>
  >(`${RESOURCE}/meta-commerce/cart-draft`, payload);

/** Whether saved credentials exist (inputs stay empty in the UI). */
export const isCredentialsConfigured = (
  integration: TenantIntegration | null | undefined
): boolean => integration?.configured === true;
